<template>
  <div class="attachments-section mt-4">
    <legend>
      <strong>{{ $t('attachments') }}</strong>
    </legend>

    <template v-if="!isReadOnly">
      <div v-if="isSaved">
        <b-form-group
          horizontal
          :label="$t('attachmentType')"
          label-for="attachmentType"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <multiselect
            :placeholder="$t('attachmentType')"
            v-model="selectedAttachmentType"
            id="attachmentType"
            :options="attachmentTypes"
            :allow-empty="false"
            :show-labels="false"
          >
            <template slot="singleLabel" slot-scope="{ option }">
              {{ $t(option) }}
            </template>
            <template slot="option" slot-scope="{ option }">
              {{ $t(option) }}
            </template>
          </multiselect>

          <DropzoneUpload
            class="mt-2"
            :complete="isAttachmentUploadComplete"
            :parentId="parentId"
            :parentType="selectedAttachmentType"
            :uploadCallback="uploadCallback"
            :disabled="selectedAttachmentType === null"
            v-on:dropzone-upload:reset="isAttachmentUploadComplete = false"
          />
        </b-form-group>
      </div>

      <p v-else class="text-muted">
        {{ $t('save_before_upload') }}
      </p>
    </template>

    <!-- Attachment Lists by Type -->
    <div v-for="attachmentType in attachmentTypes" :key="attachmentType">
      <legend v-if="attachmentsOfType(attachmentType).length > 0">
        <strong>{{ $t(attachmentType) }}</strong>
      </legend>
      <AttachmentList
        :attachments="attachmentsOfType(attachmentType)"
        :attachmentType="attachmentType"
        :deletable="!isReadOnly"
        v-on:attachment-list:delete="$emit('delete-attachment', $event.attachmentId)"
      />
    </div>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect';
import AttachmentList from '@/components/AttachmentList.vue';
import DropzoneUpload from '@/components/DropzoneUpload.vue';

const UNSAVED_ID = '_new';

/**
 * Typed attachments of an invoice or offer: upload once the record is saved, one list per type.
 * Read-only keeps the lists to open the files.
 */
export default {
  name: 'AttachmentsSection',
  components: {
    Multiselect,
    AttachmentList,
    DropzoneUpload,
  },
  props: {
    /** Id of the record; empty or `_new` while it is not saved */
    parentId: {
      type: String,
      default: null,
    },
    attachments: {
      type: Array,
      default: () => [],
    },
    attachmentTypes: {
      type: Array,
      required: true,
    },
    isReadOnly: {
      type: Boolean,
      default: false,
    },
    /** Store action that uploads one file (DropzoneUpload `uploadCallback`) */
    uploadCallback: {
      type: Function,
      required: true,
    },
    /** Name of that store action; the upload counts as complete after it */
    uploadActionType: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isAttachmentUploadComplete: false,
      selectedAttachmentType: this.attachmentTypes[0] ?? null,
    };
  },
  computed: {
    isSaved() {
      return Boolean(this.parentId) && this.parentId !== UNSAVED_ID;
    },
  },
  methods: {
    attachmentsOfType(attachmentType) {
      return (this.attachments ?? []).filter(
        (attachment) => attachment.attachmentType === attachmentType
      );
    },
  },
  mounted() {
    // After upload is complete clear the dropzone preview
    this.unsubscribeAction = this.$store.subscribeAction({
      after: (action) => {
        if (action.type === this.uploadActionType) {
          this.isAttachmentUploadComplete = true;
        }
      },
    });
  },
  beforeDestroy() {
    this.unsubscribeAction();
  },
};
</script>

<style scoped lang="scss">
.attachments-section {
  border-top: 1px solid #dee2e6;
  padding-top: 1rem;
}
</style>
